import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PaginationCoreProps {
  paginationModel: {
    page: number;
    pageSize: number;
  };
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showQuickJump?: boolean;
  showFirstLast?: boolean;
  className?: string;
}

const paginationButtonClassName =
  'h-8 min-w-8 rounded-md px-2 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900';

export function PaginationCore({
  paginationModel,
  totalItems,
  onPageChange,
  showFirstLast = false,
  className
}: PaginationCoreProps) {
  const effectiveTotalItems = totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalItems / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const startItem = effectiveTotalItems > 0 ? currentPage * paginationModel.pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * paginationModel.pageSize, effectiveTotalItems);

  const visiblePages = useMemo(() => {
    const pages: Array<number | 'ellipsis'> = [];
    const maxVisiblePages = 5;
    const current = currentPage + 1;

    let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('ellipsis');
      }
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('ellipsis');
      }
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  if (effectiveTotalItems === 0) {
    return null;
  }

  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const goToPage = (page: number) => {
    const clampedPage = Math.max(0, Math.min(page, totalPages - 1));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div className={cn('ml-auto flex h-8 flex-shrink-0 items-center justify-end gap-2 pr-2', className)}>
      <div className='text-xs text-slate-500'>
        <span className='font-semibold text-slate-900'>
          {startItem.toLocaleString()} - {endItem.toLocaleString()}
        </span>
        {' trong '}
        <span className='font-semibold text-slate-900'>{effectiveTotalItems.toLocaleString()}</span>
      </div>

      <div className='text-xs text-slate-500'>
        Trang <span className='font-semibold text-slate-900'>{currentPage + 1}</span> /{' '}
        <span className='font-semibold text-slate-900'>{totalPages}</span>
      </div>

      <Separator orientation='vertical' className='h-4 bg-slate-200' />

      <div className='flex items-center gap-0.5'>
        {showFirstLast && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className={paginationButtonClassName}
            onClick={() => goToPage(0)}
            disabled={!canGoPrevious}
          >
            <ChevronsLeft className='size-4' />
          </Button>
        )}

        <Button
          type='button'
          variant='ghost'
          size='icon'
          className={paginationButtonClassName}
          onClick={() => goToPage(currentPage - 1)}
          disabled={!canGoPrevious}
        >
          <ChevronLeft className='size-4' />
        </Button>

        {visiblePages.map((page, index) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className='px-1 text-xs text-slate-400'>
              ...
            </span>
          ) : (
            <Button
              key={page}
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => goToPage(page - 1)}
              className={cn(
                paginationButtonClassName,
                'min-w-8 px-2',
                page === currentPage + 1 && 'font-bold text-slate-950 hover:bg-transparent hover:text-slate-950'
              )}
            >
              {page}
            </Button>
          )
        )}

        <Button
          type='button'
          variant='ghost'
          size='icon'
          className={paginationButtonClassName}
          onClick={() => goToPage(currentPage + 1)}
          disabled={!canGoNext}
        >
          <ChevronRight className='size-4' />
        </Button>

        {showFirstLast && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className={paginationButtonClassName}
            onClick={() => goToPage(totalPages - 1)}
            disabled={!canGoNext}
          >
            <ChevronsRight className='size-4' />
          </Button>
        )}
      </div>
    </div>
  );
}
